import { supabase } from '../lib/supabase';
import { shippingService } from './shippingService';
import { analytics } from '../lib/posthog';

export interface CartItem {
  id?: string;
  user_id: string;
  product_id: string;
  quantity: number;
  created_at?: string;
  product?: {
    id: string;
    name: string;
    price: number;
    image_url: string;
    description?: string;
  };
}

export const cartService = {
  /**
   * Calcular frete - INTEGRADO COM SHIPPING SERVICE
   * Retorna null se o país não é suportado ou código postal inválido
   */
  async calculateShipping(items: CartItem[], zipCode?: string | null): Promise<number | null> {
    if (!zipCode || zipCode.length < 4) {
      return null; // Sem código postal válido
    }

    // Calcular total do carrinho
    const cartTotal = items.reduce((sum, item) => {
      const price = item.product?.price || 0;
      return sum + (price * item.quantity);
    }, 0);

    // Calcular frete usando o shipping service
    // Retorna null se país não detectado ou zona não encontrada
    const shippingCost = await shippingService.calculateCartShipping(zipCode, cartTotal);
    
    return shippingCost;
  },

  // Buscar itens do carrinho do usuário
  async getCartItems(userId: string): Promise<CartItem[]> {
    try {
      // Buscar itens do carrinho
      const { data: cartItems, error: cartError } = await supabase
        .from('cart_items')
        .select('id, user_id, product_id, quantity, created_at')
        .eq('user_id', userId);

      if (cartError) {
        console.error('Erro ao buscar carrinho:', cartError);
        return [];
      }

      if (!cartItems || cartItems.length === 0) {
        return [];
      }

      // Buscar produtos separadamente
      const productIds = cartItems.map(item => item.product_id);
      const { data: products, error: productsError } = await supabase
        .from('products')
        .select('id, name, price, image_url, description')
        .in('id', productIds);

      if (productsError) {
        console.error('Erro ao buscar produtos:', productsError);
        return [];
      }

      // Mapear produtos aos itens do carrinho
      const mappedData = cartItems.map(item => {
        const product = products?.find(p => p.id === item.product_id);
        return {
          ...item,
          product: product ? {
            id: product.id,
            name: product.name,
            price: product.price,
            image_url: product.image_url,
            description: product.description
          } : undefined
        };
      });

      return mappedData;
    } catch (error) {
      console.error('Erro ao buscar carrinho:', error);
      return [];
    }
  },

  // Adicionar item ao carrinho
  async addToCart(userId: string, productId: string, quantity: number): Promise<boolean> {
    try {
      console.log('🛒 Adicionando ao carrinho:', { userId, productId, quantity });
      
      const { data: existingItems, error: searchError } = await supabase
        .from('cart_items')
        .select('id, quantity')
        .eq('user_id', userId)
        .eq('product_id', productId);

      if (searchError) {
        console.error('❌ Erro ao verificar item existente:', searchError);
        return false;
      }

      if (existingItems && existingItems.length > 0) {
        console.log('📝 Item já existe, atualizando quantidade...');
        const existingItem = existingItems[0];

        const { error: updateError } = await supabase
          .from('cart_items')
          .update({ quantity: existingItem.quantity + quantity })
          .eq('id', existingItem.id);

        if (updateError) {
          console.error('❌ Erro ao atualizar quantidade:', updateError);
          return false;
        }

        console.log('✅ Quantidade atualizada');
        return true;
      } else {
        console.log('➕ Criando novo item no carrinho...');

        const { data, error: insertError } = await supabase
          .from('cart_items')
          .insert({
            user_id: userId,
            product_id: productId,
            quantity: quantity
          })
          .select();

        if (insertError) {
          console.error('❌ Erro ao criar item:', insertError);
          return false;
        }

        // Get product details for tracking
        const { data: product } = await supabase
          .from('products')
          .select('id, name, price')
          .eq('id', productId)
          .single();

        if (product) {
          analytics.addToCart({
            id: product.id,
            name: product.name,
            price: product.price,
            quantity: quantity,
          });
        }

        console.log('✅ Item criado:', data);
        return true;
      }
    } catch (error) {
      console.error('❌ Erro ao adicionar ao carrinho:', error);
      return false;
    }
  },

  // Atualizar quantidade
  async updateQuantity(itemId: string, quantity: number): Promise<boolean> {
    if (quantity <= 0) {
      return this.removeItem(itemId);
    }

    const { error } = await supabase
      .from('cart_items')
      .update({ quantity })
      .eq('id', itemId);

    if (error) {
      console.error('Erro ao atualizar quantidade:', error);
      return false;
    }

    return true;
  },

  // Remover item
  async removeItem(itemId: string): Promise<boolean> {
    const { error } = await supabase
      .from('cart_items')
      .delete()
      .eq('id', itemId);

    if (error) {
      console.error('Erro ao remover item:', error);
      return false;
    }

    analytics.removeFromCart(itemId);
    return true;
  },

  // Limpar carrinho
  async clearCart(userId: string): Promise<boolean> {
    const { error } = await supabase
      .from('cart_items')
      .delete()
      .eq('user_id', userId);

    if (error) {
      console.error('Erro ao limpar carrinho:', error);
      return false;
    }

    return true;
  },

  // Buscar produto por ID (para carrinho guest)
  async getProductById(productId: string) {
    try {
      const { data: product, error } = await supabase
        .from('products')
        .select('id, name, price, image_url, description')
        .eq('id', productId)
        .single();

      if (error) {
        console.error('Erro ao buscar produto:', error);
        return null;
      }

      return product;
    } catch (error) {
      console.error('Erro ao buscar produto:', error);
      return null;
    }
  }
};
